import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import React, { useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import Messages from "../../Shared/Messages";

/**
 * Generic branded "clinical block" for the document design system. One atom node
 * covers several block types, configured via a dialog and stored as JSON on a
 * `data-config` attribute. `renderHTML` emits the FINAL branded markup (the
 * `.doc-table` / `.doc-grid` / `.doc-check` / `.doc-signature` classes) so the
 * saved template HTML renders correctly in the generated PDF and round-trips
 * back into the node on load. In the editor it shows a friendly summary card.
 *
 * Block types:
 *  - refTable   Reference-range lab table (Test / Result / Range / Flag). Result
 *               cells bind to a patient attribute; the flag is computed at render
 *               time by the ai-service from data-low / data-high.
 *  - fieldGrid  Two-column label + value grid (patient details).
 *  - tickbox    Checklist of labelled tick boxes.
 *  - signature  Sign-off lines (label under each rule).
 */

const DEFAULT_COLUMNS = {
  measure: "Measurement",
  result: "Result",
  interp: "Interpretation",
};

// `msgId` is a Messages key (string, not a captured descriptor — avoids module-init
// ordering issues); resolved via Messages[msgId] at the use site.
const BLOCK_META = {
  refTable: { icon: "fa-table", msgId: "doc_reference_range_table" },
  fieldGrid: { icon: "fa-th-large", msgId: "doc_field_grid" },
  tickbox: { icon: "fa-check-square", msgId: "doc_tickbox_list" },
  signature: { icon: "fa-pen-nib", msgId: "doc_signature" },
  customHtml: { icon: "fa-code", msgId: "doc_block_custom_html" },
};

// A result/value cell child spec: bound field placeholder, static text, or a
// fillable box when neither is provided.
function valueChild(row) {
  if (row.field) {
    return [
      "span",
      {
        field: row.field,
        "data-field-placeholder": row.field,
        class: "template-field-placeholder",
      },
      row.fieldLabel || row.field,
    ];
  }
  if (row.value) return row.value;
  return ["span", { class: "inp" }];
}

function renderRefTable(config) {
  const cols = { ...DEFAULT_COLUMNS, ...(config.columns || {}) };
  const head = [
    "tr",
    {},
    ["th", {}, cols.measure],
    ["th", { class: "c" }, cols.result],
    ["th", { class: "c" }, cols.interp],
  ];
  const body = (config.rows || []).map((row) => {
    // Interpretation cell is filled at render time from the observation's
    // configured interpretation label (ai-service). Blank when unbound.
    const interpCell = row.field
      ? ["td", { class: "c interp-cell", "data-interp-field": row.field }]
      : ["td", { class: "c" }];
    return [
      "tr",
      {},
      ["td", { class: "test" }, row.label || ""],
      ["td", { class: "c" }, valueChild(row)],
      interpCell,
    ];
  });
  return ["table", { class: "doc-table" }, ["thead", {}, head], ["tbody", {}, ...body]];
}

function renderFieldGrid(config) {
  const fields = (config.rows || []).map((row) => {
    // A row with neither a bound attribute nor a static value is a fill-in
    // field: completed by staff in the review editor (then baked flat into the
    // PDF). Marked .mps-fillable so the review NodeView makes it editable.
    const fillable = !row.field && !row.value;
    const box = fillable
      ? ["div", { class: "box mps-fillable" }]
      : ["div", { class: "box" }, valueChild(row)];
    return ["div", { class: "doc-field" }, ["label", {}, row.label || ""], box];
  });
  return ["div", { class: "doc-grid" }, ...fields];
}

function renderTickbox(config) {
  const items = (config.items || []).map((it) => [
    "span",
    { class: "doc-check" },
    ["span", { class: `bx${it.checked ? " checked" : ""}` }, it.checked ? "✓" : ""],
    " " + (it.label || ""),
  ]);
  return ["div", { class: "doc-checklist" }, ...items];
}

function renderSignature(config) {
  const lines = (config.lines || []).map((ln) => {
    // data-sig-auto lines are stamped at finalisation (name / current date).
    const lineAttrs = ln.auto
      ? { class: "line", "data-sig-auto": ln.auto }
      : { class: "line" };
    return [
      "div",
      { class: "doc-field" },
      ["div", lineAttrs],
      ["label", {}, ln.label || ""],
    ];
  });
  return ["div", { class: "doc-signature" }, ["div", { class: "doc-grid" }, ...lines]];
}

function renderBody(blockType, config) {
  switch (blockType) {
    case "refTable":
      return renderRefTable(config);
    case "fieldGrid":
      return renderFieldGrid(config);
    case "tickbox":
      return renderTickbox(config);
    case "signature":
      return renderSignature(config);
    default:
      return ["div", {}];
  }
}

const _esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// A bound result/value cell at design time: show the attribute as a chip, the
// static value as text, or a "fill-in" placeholder.
function _valueCellHtml(row) {
  if (row.field)
    return `<span class="template-field-placeholder">${_esc(row.fieldLabel || row.field)}</span>`;
  if (row.value) return _esc(row.value);
  return "";
}

// HTML for the actual block, rendered live in the designer (design-time: bound
// fields show as chips, interpretation/auto values show as placeholders).
function blockBodyHtml(blockType, cfg) {
  if (blockType === "customHtml") return cfg.html || "";
  if (blockType === "fieldGrid") {
    const rows = (cfg.rows || [])
      .map((r) => {
        const fillable = !r.field && !r.value;
        return `<div class="doc-field"><label>${_esc(r.label)}</label><div class="box${
          fillable ? " mps-fillable" : ""
        }">${_valueCellHtml(r)}</div></div>`;
      })
      .join("");
    return `<div class="doc-grid">${rows}</div>`;
  }
  if (blockType === "refTable") {
    const cols = { ...DEFAULT_COLUMNS, ...(cfg.columns || {}) };
    const body = (cfg.rows || [])
      .map(
        (r) =>
          `<tr><td class="test">${_esc(r.label)}</td><td class="c">${
            r.field
              ? `<span class="template-field-placeholder">${_esc(r.fieldLabel || r.field)}</span>`
              : "—"
          }</td><td class="c"><span class="doc-ph">interpretation</span></td></tr>`
      )
      .join("");
    return `<table class="doc-table"><thead><tr><th>${_esc(cols.measure)}</th><th class="c">${_esc(
      cols.result
    )}</th><th class="c">${_esc(
      cols.interp
    )}</th></tr></thead><tbody>${body}</tbody></table>`;
  }
  if (blockType === "tickbox") {
    const items = (cfg.items || [])
      .map(
        (it) =>
          `<span class="doc-check"><span class="bx${it.checked ? " checked" : ""}">${
            it.checked ? "✓" : ""
          }</span> ${_esc(it.label)}</span>`
      )
      .join("");
    return `<div class="doc-checklist">${items}</div>`;
  }
  if (blockType === "signature") {
    const lines = (cfg.lines || [])
      .map((ln) => {
        const ph =
          ln.auto === "name"
            ? '<span class="doc-ph">logged-in user</span>'
            : ln.auto === "date"
            ? '<span class="doc-ph">current date</span>'
            : "";
        return `<div class="doc-field"><div class="line">${ph}</div><label>${_esc(
          ln.label
        )}</label></div>`;
      })
      .join("");
    return `<div class="doc-signature"><div class="doc-grid">${lines}</div></div>`;
  }
  return "";
}

// Read-only in review EXCEPT for fill-in fields (.mps-fillable): those are made
// editable so staff can complete them; the typed text is committed back into the
// node's frozenHtml on blur, so it serialises verbatim into the flat PDF.
const FrozenBlock = ({ node, updateAttributes }) => {
  const bodyRef = useRef(null);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    // Set the generated HTML once (imperatively) so committing edits doesn't
    // re-render and clobber the caret.
    body.innerHTML = node.attrs.frozenHtml || "";

    // Keep edit events out of ProseMirror's keymap/selection.
    const stop = (e) => e.stopPropagation();
    const commit = () => updateAttributes({ frozenHtml: body.innerHTML });
    const cleanups = [];

    // Fill-in fields: editable text.
    body.querySelectorAll(".mps-fillable").forEach((el) => {
      el.setAttribute("contenteditable", "true");
      el.classList.add("mps-fillable-editable");
      el.addEventListener("keydown", stop);
      el.addEventListener("keyup", stop);
      el.addEventListener("paste", stop);
      el.addEventListener("blur", commit);
      cleanups.push(() => {
        el.removeEventListener("keydown", stop);
        el.removeEventListener("keyup", stop);
        el.removeEventListener("paste", stop);
        el.removeEventListener("blur", commit);
      });
    });

    // Tick-box items: clickable to toggle, committed back to frozenHtml.
    body.querySelectorAll(".doc-check").forEach((el) => {
      el.classList.add("mps-check-editable");
      const onToggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const bx = el.querySelector(".bx");
        if (!bx) return;
        const checked = bx.classList.toggle("checked");
        bx.textContent = checked ? "✓" : "";
        commit();
      };
      el.addEventListener("mousedown", stop);
      el.addEventListener("click", onToggle);
      cleanups.push(() => {
        el.removeEventListener("mousedown", stop);
        el.removeEventListener("click", onToggle);
      });
    });

    return () => cleanups.forEach((fn) => fn());
    // Mount once: the body is managed imperatively from here on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NodeViewWrapper
      className="template-doc-block-frozen"
      contentEditable={false}
      style={{ cursor: "default" }}
    >
      <div className="frozen-body" ref={bodyRef} />
    </NodeViewWrapper>
  );
};

const BlockComponent = ({ node, getPos, extension, updateAttributes }) => {
  const intl = useIntl();
  const { blockType, config } = node.attrs;
  const isReadonly = !!(extension && extension.options && extension.options.readonly);

  // Review context: render frozen (with editable fill-in fields). See FrozenBlock.
  if (isReadonly) {
    return <FrozenBlock node={node} updateAttributes={updateAttributes} />;
  }

  const meta = BLOCK_META[blockType] || { icon: "fa-cube" };
  const metaLabel = meta.msgId ? intl.formatMessage(Messages[meta.msgId]) : "Block";
  let cfg = {};
  try {
    cfg = JSON.parse(config || "{}");
  } catch (e) {
    cfg = {};
  }

  const handleClick = (e) => {
    e.stopPropagation();
    if (extension && extension.options && extension.options.onEdit) {
      extension.options.onEdit({ position: getPos(), blockType, config: cfg });
    }
  };

  // Designer: render the ACTUAL block (live) with an edit affordance, rather
  // than a summary card. Click anywhere to open the config dialog.
  return (
    <NodeViewWrapper
      className="tip-block-live"
      contentEditable={false}
      onClick={handleClick}
    >
      <span className="tip-block-edit">
        <i className={`fa ${meta.icon}`}></i> {metaLabel}
        <span className="tip-block-edit-action">
          <i className="fa fa-pen"></i> Edit
        </span>
      </span>
      <div
        className="tip-block-body"
        dangerouslySetInnerHTML={{ __html: blockBodyHtml(blockType, cfg) }}
      />
    </NodeViewWrapper>
  );
};

export const DocBlock = Node.create({
  name: "docBlock",
  group: "block",
  atom: true,

  addOptions() {
    return { onEdit: null, readonly: false };
  },

  addAttributes() {
    return {
      blockType: {
        default: "refTable",
        parseHTML: (el) => el.getAttribute("data-block-type") || "refTable",
        renderHTML: (attrs) => ({ "data-block-type": attrs.blockType }),
      },
      config: {
        // JSON string; source of truth for the rendered markup (authoring).
        default: "{}",
        parseHTML: (el) => el.getAttribute("data-config") || "{}",
        renderHTML: (attrs) => ({ "data-config": attrs.config || "{}" }),
      },
      // Captured generated inner HTML — used only in readonly (review) mode to
      // freeze and re-emit the populated content verbatim. Not serialized as an
      // attribute (re-emitted as element content in renderHTML instead).
      frozenHtml: {
        default: null,
        parseHTML: (el) => el.innerHTML,
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-doc-block]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = mergeAttributes(HTMLAttributes, {
      "data-doc-block": "",
      class: "doc-block",
    });

    // Readonly (review) mode: re-emit the captured generated HTML verbatim so the
    // populated values survive to the saved PDF. Build a real DOM node (a valid
    // DOMOutputSpec) since the content is a raw HTML string.
    const readonly = this.options && this.options.readonly;
    if (readonly && node.attrs.frozenHtml != null) {
      const dom = document.createElement("div");
      Object.entries(attrs).forEach(([k, v]) => {
        if (v != null) dom.setAttribute(k, v);
      });
      dom.innerHTML = node.attrs.frozenHtml;
      return dom;
    }

    let cfg = {};
    try {
      cfg = JSON.parse(node.attrs.config || "{}");
    } catch (e) {
      cfg = {};
    }
    if (node.attrs.blockType === "customHtml") {
      const dom = document.createElement("div");
      Object.entries(attrs).forEach(([k, v]) => {
        if (v != null) dom.setAttribute(k, v);
      });
      dom.innerHTML = cfg.html || "";
      return dom;
    }
    return ["div", attrs, renderBody(node.attrs.blockType, cfg)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(BlockComponent);
  },

  addCommands() {
    return {
      insertDocBlock:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export default DocBlock;
